import { useState } from 'react';
import {
  AlertBanner,
  Badge,
  Button,
  Dialog,
  EmptyState,
  Input,
  Select,
  StatCard,
  Toast,
} from '@assist/ui';
import { renderProblem } from '@assist/i18n';
import {
  ApiError,
  endpoints,
  type FirstManagerInvitationResult,
  type TenantRow,
} from '../state/api.js';
import { useSession } from '../state/session.js';
import { useResource } from '../lib/useResource.js';
import { Screen } from '../components/Screen.js';
import { formatDate, formatDateTime } from '../lib/format.js';
import { adaBarisTanpaSesi, pelakuJejakAudit } from '../lib/jejak-audit.js';
/**
 * GELOMBANG M15. Kesepuluh kartu dan dua panel di bawah tinggal di berkasnya sendiri —
 * `Platform.tsx` sudah memuat tujuh layar — tetapi keduanya terdaftar BERSAMA pada baris
 * `berkas` di `docs/kontrak-elemen-figma.json`, sehingga gerbang elemen membandingkan
 * kontrak rancangan dengan keduanya. Yang dikontrak adalah rancangannya, bukan tempat
 * tinggal kodenya.
 */
import {
  KartuAksesKonsultan,
  KartuLogPenyediaan,
  KartuPemakaian,
  KartuSiklusHidup,
  PanelJangkarDanEkspor,
  PanelPekerjaanDanInsiden,
} from './PlatformM15.js';

/**
 * LIMA LAYAR KONSOL PLATFORM — M15, seluruhnya izin PLT.
 *
 *   Platform — Tenants                  `42:2`
 *   Platform — Tenant Detail            `67:56`
 *   Platform — Modules & Entitlements   `19:2`
 *   Platform — Audit Trail              `43:2`
 *   Platform — System Health            `43:425`
 *
 * KELIMANYA DIBANGUN TANPA SATU PUN HANDLER BARU, dan itu temuan yang perlu dicatat:
 * rutenya sudah ada dan bekerja sejak tahap 1, dan yang tidak ada adalah layarnya. Admin
 * platform karena itu mendarat di `ScreenNotBuiltState` di atas API yang lengkap — bentuk
 * kebalikan dari UAT U2-03, dan sama membingungkannya.
 *
 * SATU HAL YANG MENGIKAT KELIMANYA: mereka satu-satunya pengecualian isolasi yang
 * DINYATAKAN pemilik proyek (peta §5.8, kelas `PLATFORM` di matriks isolasi). Jadi setiap
 * layar di berkas ini menuliskannya — karena sebuah layar yang memperlihatkan daftar
 * pelanggan tanpa menyatakan bahwa ia pengecualian mengajari pemakainya bahwa lintas-tenant
 * itu normal.
 */

/**
 * Zona waktu yang dapat dipilih. Ia daftar TERTUTUP, dan nilainya adalah pengenal IANA
 * apa adanya — istilah teknis tidak diterjemahkan, dan sebuah kotak isian bebas untuk
 * zona waktu menghasilkan `Asia/Jakata` yang baru ketahuan berbulan-bulan kemudian pada
 * laporan yang jamnya bergeser. Ketiganya menutup seluruh Indonesia: WIB, WITA, WIT.
 */
const ZONA_WAKTU = [
  { value: 'Asia/Jakarta', label: 'Asia/Jakarta (WIB)' },
  { value: 'Asia/Makassar', label: 'Asia/Makassar (WITA)' },
  { value: 'Asia/Jayapura', label: 'Asia/Jayapura (WIT)' },
] as const;

/** Platform — Tenants · `42:2` · rute `/platform/tenants`. */
export function PlatformTenants({ onOpen }: { onOpen: (tenantId: string) => void }) {
  const { t, locale, translator } = useSession();
  const data = useResource(async () => {
    const [tenants, plans] = await Promise.all([
      endpoints.platformTenants(),
      endpoints.platformPlans(),
    ]);
    return { items: tenants.items, plans: plans.items };
  }, []);

  /**
   * FORMULIR ORGANISASI BARU — `Button` `107:1243` ("Add tenant") di kepala frame
   * `42:313`, yang sudah ada di Figma sejak awal dan TIDAK ADA di kode sampai hari ini.
   *
   * Formulir isiannya sendiri TIDAK punya frame Figma. Ia mengikuti component set yang
   * sudah ada — `Dialog` `102:98` Type=Confirm, `Input` `14:11`, `Dropdown` `164:11`,
   * tombol `13:15` ber-`radius/pill` — dan tidak memperkenalkan satu pun pola baru.
   */
  const [membuat, setMembuat] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<ApiError | null>(null);
  const [slug, setSlug] = useState('');
  const [nama, setNama] = useState('');
  const [namaHukum, setNamaHukum] = useState('');
  const [paket, setPaket] = useState('');
  const [bahasa, setBahasa] = useState<'id-ID' | 'en-US'>('id-ID');
  const [zona, setZona] = useState<string>(ZONA_WAKTU[0].value);

  function bukaFormulir() {
    setProblem(null);
    setSlug('');
    setNama('');
    setNamaHukum('');
    setBahasa('id-ID');
    setZona(ZONA_WAKTU[0].value);
    setMembuat(true);
  }

  /**
   * Sesudah organisasinya dibuat, layar berpindah ke DETAILNYA — bukan kembali ke
   * daftar. Tiga langkah masih tersisa, ketiganya ada di halaman itu, dan sebuah alur
   * yang memulangkan orang ke daftar menyuruhnya mencari sendiri apa yang baru saja ia
   * buat untuk melanjutkan pekerjaan yang belum selesai.
   */
  async function buatOrganisasi() {
    setBusy(true);
    setProblem(null);
    try {
      const dibuat = await endpoints.createTenant({
        slug: slug.trim(),
        name: nama.trim(),
        legal_name: namaHukum.trim() === '' ? null : namaHukum.trim(),
        plan_code: paket,
        default_locale: bahasa,
        timezone: zona,
      });
      setMembuat(false);
      await data.reload();
      onOpen(dibuat.id);
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Screen
      title={t('platform.tenants.title')}
      subtitle={t('platform.tenants.subtitle')}
      resource={data}
      actions={
        <Button variant="primary" onClick={bukaFormulir}>
          {t('platform.tenants.add')}
        </Button>
      }
    >
      {({ items, plans }) => (
        <>
          <AlertBanner
            kind="information"
            signalWord={t('signal.information')}
            title={t('platform.isolationException')}
          />

          <div className="stat-grid">
            <StatCard label={t('platform.stat.tenants')} value={String(items.length)} />
            <StatCard
              label={t('platform.stat.active')}
              value={String(items.filter((x) => x.state === 'ACTIVE').length)}
            />
            <StatCard
              label={t('platform.stat.provisioning')}
              value={String(items.filter((x) => x.state === 'PROVISIONING').length)}
              caption={t('platform.stat.provisioning.caption')}
            />
          </div>

          {items.length === 0 ? (
            /*
              "Klien pertama dibuat dari sini." kini BENAR. Kalimatnya tidak berubah;
              yang berubah adalah bahwa keadaan kosong ini punya kendali yang
              melakukannya. `tools/ci/gerbang-janji-kendali.mjs` menuntutnya.
            */
            <EmptyState
              title={t('platform.tenants.empty.title')}
              body={t('platform.tenants.empty.body')}
              action={{ label: t('platform.tenants.empty.action'), onClick: bukaFormulir }}
            />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('platform.column.tenant')}</th>
                  <th scope="col">{t('platform.column.slug')}</th>
                  <th scope="col">{t('platform.column.plan')}</th>
                  <th scope="col">{t('platform.column.state')}</th>
                  <th scope="col">{t('platform.column.activated')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <button className="linkish" type="button" onClick={() => onOpen(row.id)}>
                        {row.name}
                      </button>
                    </td>
                    <td>{row.slug}</td>
                    <td>{row.plan_code ?? '—'}</td>
                    <td>
                      <Badge badgeTone={toneOfTenant(row.state)}>
                        {t(`platform.tenantState.${row.state}`)}
                      </Badge>
                    </td>
                    <td>{row.activated_at ? formatDate(row.activated_at, locale) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/*
            KALIMAT `platform.tenants.actionsPending` DIHAPUS PADA GELOMBANG M15.
            "Menangguhkan organisasi dan mengubah paketnya belum dapat dilakukan dari
            sini" berhenti benar pada hari kedua kendalinya lahir, dan kalimat yang
            tertinggal sesudah kendalinya ada adalah kebohongan yang arahnya berlawanan
            dari yang `gerbang-janji-kendali.mjs` larang — gerbang itu tidak dapat
            melihatnya, karena pengingkaran ("belum") membuatnya hijau selamanya di
            matanya. Keduanya kini berdiri di layar DETAIL organisasi, tempat frame
            `67:56` memang menggambarnya (tombol `Suspend tenant` `107:1267`).

            Tombol `Export` (`107:1241`) di kepala layar ini tetap TIDAK dibangun: tidak
            ada rute ekspor tenant di seluruh manifest, dan sebuah tombol yang tidak
            melakukan apa pun adalah cacat yang sama dari arah sebaliknya.
          */}

          <Dialog
            open={membuat}
            kind="confirm"
            title={t('platform.newTenant.title')}
            description={t('platform.newTenant.describe')}
            confirmLabel={t('platform.newTenant.submit')}
            cancelLabel={t('app.cancel')}
            busy={busy}
            confirmDisabled={slug.trim() === '' || nama.trim() === '' || paket === ''}
            onConfirm={() => void buatOrganisasi()}
            onCancel={() => setMembuat(false)}
          >
            {problem ? (
              <AlertBanner
                kind="danger"
                signalWord={t('signal.danger')}
                title={renderProblem(translator, problem.problem)}
              />
            ) : null}
            <Input
              label={t('platform.newTenant.slug')}
              hint={t('platform.newTenant.slug.hint')}
              value={slug}
              required
              onChange={(e) => setSlug(e.target.value)}
            />
            <Input
              label={t('platform.newTenant.name')}
              hint={t('platform.newTenant.name.hint')}
              value={nama}
              required
              onChange={(e) => setNama(e.target.value)}
            />
            <Input
              label={t('platform.newTenant.legalName')}
              hint={t('platform.newTenant.legalName.hint')}
              value={namaHukum}
              onChange={(e) => setNamaHukum(e.target.value)}
            />
            <Select
              label={t('platform.newTenant.plan')}
              options={plans.map((p) => ({ value: p.code, label: p.name }))}
              placeholder={t('platform.newTenant.plan.choose')}
              value={paket}
              onChange={(e) => setPaket(e.target.value)}
            />
            <Select
              label={t('platform.newTenant.locale')}
              options={[
                { value: 'id-ID', label: t('app.language.id') },
                { value: 'en-US', label: t('app.language.en') },
              ]}
              value={bahasa}
              onChange={(e) => setBahasa(e.target.value === 'en-US' ? 'en-US' : 'id-ID')}
            />
            <Select
              label={t('platform.newTenant.timezone')}
              options={ZONA_WAKTU.map((z) => ({ value: z.value, label: z.label }))}
              value={zona}
              onChange={(e) => setZona(e.target.value)}
            />
          </Dialog>
        </>
      )}
    </Screen>
  );
}

/**
 * KARTU PERSIAPAN ORGANISASI — langkah 2, 3, dan 4 dari alur "organisasi baru sampai
 * dapat dipakai", pada layar Detail Organisasi.
 *
 * TIDAK ADA FRAME FIGMA UNTUK KARTU INI, DAN ITU DINYATAKAN, BUKAN DIDIAMKAN.
 * ------------------------------------------------------------------------
 * `Platform — Tenant Detail` `67:56` merancang empat kartu — Subscription, Modules
 * enabled, Recent platform events, Details/Data isolation/Primary contacts — dan tidak
 * satu pun di antaranya adalah daftar langkah persiapan. Kedua tombol yang Figma gambar
 * di kepalanya (`107:1267` "Suspend tenant", `107:1269` "Manage modules") juga bukan:
 * yang pertama tidak punya rute di seluruh manifest, yang kedua menunjuk tabel modul
 * yang sudah ada di layar yang sama.
 *
 * Kartu ini karena itu DIRANCANG DI SINI, mengikuti pola yang sudah ditetapkan keempat
 * layar platform: kerangka `card` yang sama, `Badge` untuk keadaan langkah, `Input`
 * `14:11` dan `Dropdown` `164:11` untuk isian, tombol `13:15` ber-`radius/pill`, dan
 * `AlertBanner` untuk keadaan yang perlu dibaca sebelum ditekan. Ia tidak memperkenalkan
 * satu pun pola baru, sehingga frame Figma-nya kelak adalah pekerjaan penyalinan.
 *
 * KENAPA IA KOMPONEN TERSENDIRI DAN BUKAN LIMA BLOK DI DALAM LAYARNYA
 * ------------------------------------------------------------------
 * Karena ia punya keadaannya sendiri — dua formulir dan satu tautan sekali-lihat — dan
 * menaruhnya di dalam `PlatformTenantDetail` akan menyatukan keadaan formulir undangan
 * dengan keadaan dialog entitlement. Itu bukan soal kerapian: `invitation_url` TIDAK
 * DAPAT dibaca ulang dari mana pun, dan sebuah `setState` yang tidak berhubungan yang
 * kebetulan mengosongkannya berarti tautannya hilang untuk selamanya.
 */
function KartuPersiapan({
  tenant,
  onChanged,
  undangan,
  setUndangan,
}: {
  tenant: TenantRow;
  onChanged: () => Promise<void> | void;
  /**
   * HASIL UNDANGAN DIPEGANG INDUKNYA, dan itu satu-satunya bentuk yang benar.
   *
   * `Screen` mengganti anaknya dengan `LoadingState` selama `resource.status ===
   * 'loading'`. Jadi `onChanged()` — yang memuat ulang detail organisasi supaya
   * lencana langkahnya mutakhir — MELEPAS komponen ini dari pohon, dan setiap
   * `useState` di dalamnya ikut hilang. Bentuk pertama menyimpan hasil undangan di
   * sini, dan akibatnya diukur pada tangkapan layar pertama: tautannya tampil
   * sepersekian detik, lalu halaman selesai memuat ulang dan tautannya HILANG.
   *
   * Itu bukan kerlipan kosmetik. Tautan itu SEKALI LIHAT — ia tidak dapat dibaca
   * ulang dari layar mana pun maupun dari basis data, karena `outbound_email.secret`
   * dicabut hak SELECT-nya dari peran aplikasi. Kehilangannya berarti undangannya
   * harus diterbitkan ulang, dan admin platform tidak diberi tahu apa pun tentang
   * mengapa.
   */
  undangan: FirstManagerInvitationResult | null;
  setUndangan: (nilai: FirstManagerInvitationResult | null) => void;
}) {
  const { t, translator } = useSession();
  const ob = tenant.onboarding;

  const [busy, setBusy] = useState<string | null>(null);
  const [problem, setProblem] = useState<ApiError | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const [namaPenyetuju, setNamaPenyetuju] = useState('');
  const [jabatanPenyetuju, setJabatanPenyetuju] = useState('');

  const [surel, setSurel] = useState('');
  const [namaManajer, setNamaManajer] = useState('');
  const [jabatanManajer, setJabatanManajer] = useState('');
  const [tersalin, setTersalin] = useState(false);

  const perjanjianSelesai = ob?.agreement_accepted === true;
  const penyediaanSelesai =
    ob?.readable === true && (ob.item_categories ?? 0) > 0 && (ob.workflows ?? 0) > 0;
  const manajerAda = (ob?.active_managers ?? 0) > 0;
  const undanganTerbuka = ob?.open_invitations ?? 0;

  async function jalankan(nama: string, fn: () => Promise<string>) {
    setBusy(nama);
    setProblem(null);
    try {
      const pesan = await fn();
      setToast(pesan);
      await onChanged();
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setBusy(null);
    }
  }

  /**
   * LANGKAH 3 BERJALAN DI DALAM LANGKAH 2, dan jawabannya dilaporkan apa adanya.
   * `POST …/processing-agreement` menjalankan penyediaan sesudah penerimaannya COMMIT,
   * dan mengembalikan `provisioning.ok`. Bila ia gagal, penerimaan perjanjian TETAP
   * berlaku — ia peristiwa hukum yang sudah terjadi — dan langkah 3 di bawah menyalakan
   * tombolnya sendiri.
   */
  async function catatPerjanjian() {
    await jalankan('agreement', async () => {
      const hasil = await endpoints.acceptProcessingAgreement(tenant.id, {
        accepted_by_name: namaPenyetuju.trim(),
        accepted_by_title: jabatanPenyetuju.trim(),
      });
      return hasil.provisioning.ok
        ? t('platform.provisioning.done', {
            categories: String(hasil.provisioning.item_categories ?? 0),
            workflows: String(hasil.provisioning.workflows?.length ?? 0),
          })
        : t('platform.agreement.recorded', { name: tenant.name });
    });
  }

  async function terbitkanUlang() {
    await jalankan('provisioning', async () => {
      const hasil = await endpoints.rerunProvisioning(tenant.id);
      return t('platform.provisioning.done', {
        categories: String(hasil.item_categories),
        workflows: String(hasil.workflows.length),
      });
    });
  }

  async function undangManajer() {
    await jalankan('invite', async () => {
      const hasil = await endpoints.inviteFirstManager(tenant.id, {
        email: surel.trim(),
        full_name: namaManajer.trim(),
        job_title: jabatanManajer.trim() === '' ? null : jabatanManajer.trim(),
      });
      setUndangan(hasil);
      setTersalin(false);
      return t('platform.invite.invited', { name: hasil.full_name ?? hasil.email });
    });
  }

  /**
   * Menyalin tautannya. `navigator.clipboard` TIDAK ADA pada konteks non-aman dan dapat
   * ditolak pengguna; kegagalannya karena itu tidak menampilkan apa pun yang berbunyi
   * "tersalin" — tautannya tetap terlihat utuh di layar dan dapat diblok sendiri.
   */
  async function salin(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setTersalin(true);
    } catch {
      setTersalin(false);
    }
  }

  function keadaan(selesai: boolean) {
    if (ob?.readable !== true) return null;
    return (
      <Badge badgeTone={selesai ? 'SUCCESS' : 'NEUTRAL'}>
        {t(selesai ? 'platform.setup.state.done' : 'platform.setup.state.pending')}
      </Badge>
    );
  }

  return (
    <section className="card">
      <h2 className="card__title">{t('platform.setup.title')}</h2>
      <p className="card__muted">{t('platform.setup.note')}</p>

      {ob?.readable === false ? (
        <AlertBanner
          kind="warning"
          signalWord={t('signal.warning')}
          title={t('platform.setup.unknown')}
        />
      ) : null}
      {problem ? (
        <AlertBanner
          kind="danger"
          signalWord={t('signal.danger')}
          title={renderProblem(translator, problem.problem)}
        />
      ) : null}

      {/* ---------------------------------------------------------- langkah 1 */}
      <div className="setup-step">
        <h3 className="setup-step__title">
          {t('platform.setup.step1')} {keadaan(true)}
        </h3>
        <p className="card__muted">
          {t('platform.setup.step1.body', {
            name: tenant.name,
            slug: tenant.slug,
            plan: tenant.plan_code ?? '—',
          })}
        </p>
      </div>

      {/* ---------------------------------------------------------- langkah 2 */}
      <div className="setup-step">
        <h3 className="setup-step__title">
          {t('platform.setup.step2')} {keadaan(perjanjianSelesai)}
        </h3>
        {perjanjianSelesai ? (
          <p className="card__muted">{t('platform.agreement.body')}</p>
        ) : (
          <>
            <p className="card__muted">{t('platform.agreement.body')}</p>
            <Input
              label={t('platform.agreement.name')}
              value={namaPenyetuju}
              required
              onChange={(e) => setNamaPenyetuju(e.target.value)}
            />
            <Input
              label={t('platform.agreement.jobTitle')}
              value={jabatanPenyetuju}
              required
              onChange={(e) => setJabatanPenyetuju(e.target.value)}
            />
            <Button
              variant="primary"
              busy={busy === 'agreement'}
              disabled={namaPenyetuju.trim() === '' || jabatanPenyetuju.trim() === ''}
              onClick={() => void catatPerjanjian()}
            >
              {t('platform.agreement.submit')}
            </Button>
          </>
        )}
      </div>

      {/* ---------------------------------------------------------- langkah 3 */}
      <div className="setup-step">
        <h3 className="setup-step__title">
          {t('platform.setup.step3')} {keadaan(penyediaanSelesai)}
        </h3>
        <p className="card__muted">{t('platform.provisioning.body')}</p>
        {ob?.readable === true && perjanjianSelesai ? (
          penyediaanSelesai ? (
            <p className="card__muted">
              {t('platform.provisioning.counts', {
                categories: String(ob.item_categories ?? 0),
                workflows: String(ob.workflows ?? 0),
              })}
            </p>
          ) : (
            <>
              <p className="card__muted">{t('platform.provisioning.missing')}</p>
              <Button
                variant="primary"
                busy={busy === 'provisioning'}
                onClick={() => void terbitkanUlang()}
              >
                {t('platform.provisioning.rerun')}
              </Button>
            </>
          )
        ) : null}
      </div>

      {/* ---------------------------------------------------------- langkah 4 */}
      <div className="setup-step">
        <h3 className="setup-step__title">
          {t('platform.setup.step4')} {keadaan(manajerAda || undanganTerbuka > 0)}
        </h3>
        <p className="card__muted">{t('platform.invite.body')}</p>

        {/*
          SEBUAH TOMBOL YANG DIMATIKAN TANPA PENJELASAN adalah kelas cacat yang sama
          dengan kalimat yang menjanjikan aksi tanpa kendalinya — hanya dari arah
          sebaliknya. Sebelum perjanjiannya tercatat, medan undangannya TIDAK
          ditampilkan sama sekali dan sebuah kalimat mengatakan apa yang harus terjadi
          lebih dulu; ia tidak ditampilkan dengan tombol kelabu yang membuat orang
          mengetik tiga medan lalu menemukan bahwa ia tidak dapat menekan apa pun.
        */}
        {!perjanjianSelesai ? (
          <p className="card__muted">{t('platform.invite.needsAgreement')}</p>
        ) : manajerAda ? (
          <p className="card__muted">{t('platform.invite.alreadyIn')}</p>
        ) : (
          <>
            {undanganTerbuka > 0 ? (
              <p className="card__muted">
                {t('platform.invite.pending', { count: String(undanganTerbuka) })}
              </p>
            ) : null}
            <Input
              label={t('platform.invite.email')}
              inputType="email"
              value={surel}
              required
              onChange={(e) => setSurel(e.target.value)}
            />
            <Input
              label={t('platform.invite.fullName')}
              value={namaManajer}
              required
              onChange={(e) => setNamaManajer(e.target.value)}
            />
            <Input
              label={t('platform.invite.jobTitle')}
              value={jabatanManajer}
              onChange={(e) => setJabatanManajer(e.target.value)}
            />
            <Button
              variant="primary"
              busy={busy === 'invite'}
              disabled={surel.trim() === '' || namaManajer.trim() === ''}
              onClick={() => void undangManajer()}
            >
              {t('platform.invite.submit')}
            </Button>
          </>
        )}

        {/*
          TAUTANNYA, DAN KALIMAT YANG TIDAK MENGKLAIM PENGIRIMAN.

          `ASSIST_MAIL_TRANSPORT=queue` di produksi TIDAK mengirim apa pun. Sebuah layar
          yang berbunyi "undangan terkirim" karena itu adalah UAT D-02 dalam bentuk baru,
          dan `tools/ci/gerbang-janji-kendali.mjs` aturan B menolak kalimat seperti itu di
          katalog. Yang ditampilkan adalah keadaan sebenarnya beserta tautannya, sekali.
        */}
        {undangan ? (
          <div className="setup-step__link">
            <h4 className="setup-step__title">{t('platform.invite.link.title')}</h4>
            {undangan.invitation_url ? (
              <>
                <p className="card__muted">
                  {t('platform.invite.link.notDelivered', {
                    name: undangan.full_name ?? undangan.email,
                  })}
                </p>
                <p className="card__muted">{t('platform.invite.link.once')}</p>
                <code className="setup-step__url">{undangan.invitation_url}</code>
                <Button
                  variant="secondary"
                  onClick={() => void salin(undangan.invitation_url ?? '')}
                >
                  {t('platform.invite.link.copy')}
                </Button>
                {tersalin ? (
                  <p className="card__muted">{t('platform.invite.link.copied')}</p>
                ) : null}
              </>
            ) : (
              <p className="card__muted">{t('platform.invite.link.delivered')}</p>
            )}
          </div>
        ) : null}
      </div>

      {ob?.operable === true ? (
        <AlertBanner
          kind="success"
          signalWord={t('signal.success')}
          title={t('platform.setup.done')}
        />
      ) : null}

      {toast ? (
        <div className="assist-toast-stack">
          <Toast
            kind="success"
            signalWord={t('signal.success')}
            title={toast}
            onDismiss={() => setToast(null)}
            closeLabel={t('app.dismiss')}
          />
        </div>
      ) : null}
    </section>
  );
}

/** Platform — Tenant Detail · `67:56` · rute `/platform/tenants/:id`. */
export function PlatformTenantDetail({
  tenantId,
  onBack,
}: {
  tenantId: string;
  onBack: () => void;
}) {
  const { t, locale, translator } = useSession();
  const data = useResource(
    async () => {
      /**
       * EMPAT PERMINTAAN, SATU PEMUATAN — dan `plans` DITAMBAHKAN gelombang M15 karena
       * kartu paket menyebut NAMA paket, bukan kodenya. Mengambilnya per render, atau
       * per baris, adalah bentuk N+1 pada layar yang dibuka admin platform setiap hari.
       */
      const [tenant, entitlements, modules, plans] = await Promise.all([
        endpoints.platformTenant(tenantId),
        endpoints.platformTenantEntitlements(tenantId),
        endpoints.platformModules(),
        endpoints.platformPlans(),
      ]);
      return {
        tenant,
        entitlements: entitlements.modules,
        modules: modules.items,
        plans: plans.items,
      };
    },
    [tenantId],
  );
  const [busy, setBusy] = useState<string | null>(null);
  const [problem, setProblem] = useState<ApiError | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<{ code: string; next: string } | null>(null);
  /**
   * Hasil undangan hidup DI SINI, di luar render-prop `Screen`, karena di dalamnya ia
   * tidak selamat dari satu pun pemuatan ulang. Lihat catatan panjangnya di
   * `KartuPersiapan`. Ia disimpan UTUH, bukan hanya tautannya: layar perlu tahu apakah
   * ada yang mengantarkannya (`delivery_configured`) untuk memilih kalimat yang benar,
   * dan kepada siapa tautannya harus diserahkan.
   */
  const [undangan, setUndangan] = useState<FirstManagerInvitationResult | null>(null);

  /**
   * IDENTITAS ORGANISASI, SISI PLT — Figma `216:2558` (EN) / `216:2760` (ID).
   *
   * Dua hal yang terpisah tajam di layar ini, dan pemisahannya ADR-I1:
   *
   *   · `Identitas yang mengikat kontrak` — nama badan hukum dan slug. DAPAT DISUNTING
   *     di sini dan TIDAK DI MANA PUN LAGI. Nama badan hukum muncul di perjanjian
   *     pemrosesan data yang sudah ditandatangani, jadi pihak yang menandatanganinya
   *     tidak dapat mengubahnya sendiri.
   *   · `Yang dikendalikan organisasi` — logo, warna aksen, zona waktu, bahasa bawaan.
   *     BACA-SAJA di sini. Ia ada supaya dukungan PQM dan pelanggan menatap hal yang
   *     sama ketika pelanggan menelepon, dan ia BUKAN jalan kedua untuk mengubahnya:
   *     `PATCH /v1/platform/tenants/{tenantId}` tidak mengenal keempat kunci itu, dan
   *     `.strict()` menolak permintaan yang menyebutnya alih-alih mengabaikannya.
   */
  const [legalName, setLegalName] = useState<string | null>(null);
  const [slug, setSlug] = useState<string | null>(null);
  const [namaTampilan, setNamaTampilan] = useState<string | null>(null);
  const [menyimpanIdentitas, setMenyimpanIdentitas] = useState(false);

  async function simpanIdentitas() {
    setProblem(null);
    setMenyimpanIdentitas(true);
    try {
      await endpoints.platformTenantUpdate(tenantId, {
        ...(namaTampilan !== null ? { name: namaTampilan } : {}),
        ...(legalName !== null ? { legal_name: legalName } : {}),
        ...(slug !== null ? { slug } : {}),
      });
      setLegalName(null);
      setSlug(null);
      setNamaTampilan(null);
      setToast(t('identity.saved'));
      await data.reload();
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setMenyimpanIdentitas(false);
    }
  }

  async function ubahEntitlement(moduleCode: string, next: string) {
    setBusy(moduleCode);
    setProblem(null);
    try {
      await endpoints.setEntitlement(tenantId, moduleCode, next);
      setToast(
        t(next === 'ENTITLED' ? 'platform.module.enabled' : 'platform.module.disabled', {
          module: moduleCode,
        }),
      );
      setConfirming(null);
      await data.reload();
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setBusy(null);
    }
  }

  return (
    <Screen
      title={t('platform.tenantDetail.title')}
      resource={data}
      actions={
        <Button variant="secondary" onClick={onBack}>
          {t('platform.back')}
        </Button>
      }
    >
      {(loaded) => (
        <>
          {problem ? (
            <AlertBanner
              kind="danger"
              signalWord={t('signal.danger')}
              title={renderProblem(translator, problem.problem)}
            />
          ) : null}

          <section className="card">
            <h2 className="card__title">{loaded.tenant.name}</h2>
            <dl className="facts">
              <div>
                <dt>{t('platform.column.slug')}</dt>
                <dd>{loaded.tenant.slug}</dd>
              </div>
              <div>
                <dt>{t('platform.tenantDetail.legalName')}</dt>
                <dd>{loaded.tenant.legal_name ?? '—'}</dd>
              </div>
              <div>
                <dt>{t('platform.column.plan')}</dt>
                <dd>{loaded.tenant.plan_code ?? '—'}</dd>
              </div>
              <div>
                <dt>{t('platform.column.state')}</dt>
                <dd>
                  <Badge badgeTone={toneOfTenant(loaded.tenant.state)}>
                    {t(`platform.tenantState.${loaded.tenant.state}`)}
                  </Badge>
                </dd>
              </div>
              <div>
                <dt>{t('platform.column.activated')}</dt>
                <dd>
                  {loaded.tenant.activated_at ? formatDate(loaded.tenant.activated_at, locale) : '—'}
                </dd>
              </div>
              <div>
                <dt>{t('platform.tenantDetail.users')}</dt>
                <dd>{loaded.tenant.user_count ?? '—'}</dd>
              </div>
            </dl>
          </section>

          <section className="card" data-figma="platform.tenant.identitasKontrak">
            <h2 className="card__title">{t('identity.platform.heading')}</h2>
            <p className="card__muted">{t('identity.platform.subtitle')}</p>
            <Input
              label={t('identity.platform.legalName')}
              value={legalName ?? loaded.tenant.legal_name ?? ''}
              onChange={(e) => setLegalName(e.target.value)}
            />
            <Input
              label={t('identity.platform.slug')}
              value={slug ?? loaded.tenant.slug ?? ''}
              onChange={(e) => setSlug(e.target.value)}
            />
            <Input
              label={t('identity.name.heading')}
              value={namaTampilan ?? loaded.tenant.name ?? ''}
              hint={t('identity.platform.hint')}
              onChange={(e) => setNamaTampilan(e.target.value)}
            />
            <Button
              variant="primary"
              disabled={
                menyimpanIdentitas ||
                (legalName === null && slug === null && namaTampilan === null)
              }
              onClick={() => void simpanIdentitas()}
            >
              {t('identity.save')}
            </Button>
          </section>

          <section className="card" data-figma="platform.tenant.identitasOrganisasi">
            <h2 className="card__title">{t('identity.platform.controlled')}</h2>
            <dl className="facts">
              <div>
                <dt>{t('identity.logo.label')}</dt>
                <dd>
                  {/*
                    Tanda logo `220:2189`. Ia dirender dari `branding.logo_mark_file_id`
                    lewat rute organisasinya sendiri, dan karena itu TIDAK ADA di sini
                    untuk organisasi yang belum mengunggahnya — sebuah kotak kosong
                    menyatakan "logonya rusak", yang berbeda dari "belum ada logo".

                    BATAS YANG DINYATAKAN: `GET /v1/tenant/identity` adalah rute
                    ber-lingkup SESI, jadi ia mengembalikan organisasi pemanggilnya
                    sendiri — dan sesi admin platform tidak terikat ke organisasi mana
                    pun. Yang ditampilkan di sini karena itu nilai `branding` yang sudah
                    ada di `GET /v1/platform/tenants/{tenantId}`, TANPA URL logonya:
                    menerbitkan URL unduh lintas organisasi menuntut rute platform
                    tersendiri beserta baris matriks isolasinya, dan itu gelombang
                    berikutnya. Yang tampil hari ini adalah ukuran dan keadaannya, yang
                    cukup untuk menjawab "apakah pelanggan sudah memasang logonya".
                  */}
                  {logoTerpasang(loaded.tenant) ? t('identity.logo.verified') : '—'}
                </dd>
              </div>
              <div>
                <dt>{t('identity.name.heading')}</dt>
                <dd>{loaded.tenant.name}</dd>
              </div>
              <div>
                <dt>{t('identity.accent.heading')}</dt>
                <dd data-figma="platform.tenant.logo">{aksenTenant(loaded.tenant) ?? '—'}</dd>
              </div>
              <div>
                <dt>{t('identity.locale.label')}</dt>
                <dd>{loaded.tenant.default_locale ?? '—'}</dd>
              </div>
              <div>
                <dt>{t('identity.timezone.label')}</dt>
                <dd>{loaded.tenant.timezone ?? '—'}</dd>
              </div>
            </dl>
            <p className="card__muted">{t('identity.platform.controlledHint')}</p>
          </section>

          {/*
            KARTU PERSIAPAN LEBIH DULU, di atas modul dan di bawah fakta organisasinya.
            Urutannya mengikuti apa yang sedang dikerjakan orang yang membuka layar ini:
            sebuah organisasi yang belum punya manajer belum membutuhkan tabel entitlement.
            Ia menghilang sendiri ketika keempat langkahnya selesai — lihat `ob.operable`.
          */}
          <KartuPersiapan
            tenant={loaded.tenant}
            onChanged={data.reload}
            undangan={undangan}
            setUndangan={setUndangan}
          />

          {/*
            GELOMBANG M15 — LIMA KARTU, SEPULUH PENANDA RANCANGAN, LIMA BELAS RUTE.
            Acuannya frame `223:2350` (EN) / `223:2420` (ID), yang dirancang di Figma
            SEBELUM berkas `PlatformM15.tsx` ditulis. Urutannya mengikuti apa yang
            dikerjakan orang yang membuka layar ini: keadaan organisasi dan paketnya lebih
            dulu, lalu angkanya, lalu riwayat penyediaannya, lalu siapa dari PQM yang boleh
            membacanya — yang terakhir karena ia keputusan yang paling jarang diambil dan
            paling besar akibatnya.
          */}
          <KartuSiklusHidup
            tenant={loaded.tenant}
            plans={loaded.plans}
            onChanged={data.reload}
          />
          <KartuPemakaian tenantId={tenantId} />
          <KartuLogPenyediaan tenantId={tenantId} />
          <KartuAksesKonsultan tenant={loaded.tenant} />

          <section className="card">
            <h2 className="card__title">{t('platform.tenantDetail.modules')}</h2>
            {/*
              MENYALAKAN MODUL PADA TENANT BERJALAN — janji peta §5.8: di bawah 30 menit,
              TANPA penggelaran. Ia benar-benar begitu: `PUT .../entitlements/{module}`
              membatalkan cache entitlement, dan navigasi, rute, serta laporan tenant itu
              ikut hidup pada permintaan berikutnya.
            */}
            <p className="card__muted">{t('platform.tenantDetail.modules.note')}</p>
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('platform.column.module')}</th>
                  <th scope="col">{t('platform.column.moduleName')}</th>
                  <th scope="col">{t('platform.column.state')}</th>
                  <th scope="col">{t('locations.column.action')}</th>
                </tr>
              </thead>
              <tbody>
                {loaded.modules.map((m) => {
                  const ent = loaded.entitlements.find((e) => e.code === m.code);
                  const entitled = m.is_core || ent?.state === 'ENTITLED';
                  return (
                    <tr key={m.code}>
                      <td>{m.code}</td>
                      <td>{m.name}</td>
                      <td>
                        {m.is_core ? (
                          <Badge tone="neutral">{t('platform.module.core')}</Badge>
                        ) : (
                          <Badge badgeTone={entitled ? 'SUCCESS' : 'NEUTRAL'}>
                            {t(entitled ? 'platform.module.on' : 'platform.module.off')}
                          </Badge>
                        )}
                      </td>
                      <td>
                        {m.is_core ? (
                          /* Modul inti tidak dapat dimatikan, dan tombolnya TIDAK ADA —
                             bukan mati. Sebuah tombol mati mengajak orang mencoba. */
                          <span className="card__muted">—</span>
                        ) : (
                          <Button
                            variant="text"
                            busy={busy === m.code}
                            onClick={() =>
                              setConfirming({ code: m.code, next: entitled ? 'DISABLED' : 'ENTITLED' })
                            }
                          >
                            {t(entitled ? 'platform.module.disable' : 'platform.module.enable')}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>

          {/*
            Mematikan modul adalah operasi yang AKIBATNYA perlu dibaca sebelum ditekan:
            data utuh dan dapat diekspor tetapi tidak dapat diubah, menu hilang, rute
            menjawab 403, job tidak dijadwalkan, dan poin yang sudah diberikan TIDAK
            ditarik (peta §5.8 `when_disabled`). Kalimat itu ada di dialognya.
          */}
          <Dialog
            open={confirming !== null}
            kind={confirming?.next === 'DISABLED' ? 'destructive' : 'confirm'}
            title={
              confirming?.next === 'DISABLED'
                ? t('platform.module.disable')
                : t('platform.module.enable')
            }
            description={
              confirming?.next === 'DISABLED'
                ? t('platform.module.disable.describe', { module: confirming?.code ?? '' })
                : t('platform.module.enable.describe', { module: confirming?.code ?? '' })
            }
            confirmLabel={t('app.save')}
            cancelLabel={t('app.cancel')}
            busy={busy !== null}
            onConfirm={() => {
              if (confirming) void ubahEntitlement(confirming.code, confirming.next);
            }}
            onCancel={() => setConfirming(null)}
          />

          {toast ? (
            <div className="assist-toast-stack">
              <Toast
                kind="success"
                signalWord={t('signal.success')}
                title={toast}
                onDismiss={() => setToast(null)}
                closeLabel={t('app.dismiss')}
              />
            </div>
          ) : null}
        </>
      )}
    </Screen>
  );
}

/** Platform — Modules & Entitlements · `19:2` · rute `/platform/modules`. */
export function PlatformModules() {
  const { t } = useSession();
  const data = useResource(
    async () => {
      const [modules, plans] = await Promise.all([
        endpoints.platformModules(),
        endpoints.platformPlans(),
      ]);
      return { modules: modules.items, plans: plans.items };
    },
    [],
  );

  return (
    <Screen title={t('platform.modules.title')} subtitle={t('platform.modules.subtitle')} resource={data}>
      {(loaded) => (
        <>
          <section className="card">
            <h2 className="card__title">{t('platform.modules.registry')}</h2>
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('platform.column.module')}</th>
                  <th scope="col">{t('platform.column.moduleName')}</th>
                  <th scope="col">{t('platform.column.kernel')}</th>
                  <th scope="col">{t('platform.column.entitlement')}</th>
                  <th scope="col">{t('platform.column.requires')}</th>
                </tr>
              </thead>
              <tbody>
                {loaded.modules.map((m) => (
                  <tr key={m.code}>
                    <td>
                      {m.code}
                      {m.is_core ? (
                        <>
                          {' '}
                          <Badge tone="neutral">{t('platform.module.core')}</Badge>
                        </>
                      ) : null}
                    </td>
                    <td>{m.name}</td>
                    <td>{m.kernel_owner ?? '—'}</td>
                    <td>{m.entitlement_code ?? '—'}</td>
                    <td>{(m.requires ?? []).join(', ') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="card">
            <h2 className="card__title">{t('platform.modules.plans')}</h2>
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('platform.column.plan')}</th>
                  <th scope="col">{t('platform.column.moduleName')}</th>
                  <th scope="col">{t('platform.column.module')}</th>
                </tr>
              </thead>
              <tbody>
                {loaded.plans.map((p) => (
                  <tr key={p.code}>
                    <td>{p.code}</td>
                    <td>{p.name}</td>
                    <td>{(p.modules ?? []).join(', ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <p className="card__muted">{t('platform.modules.note')}</p>
        </>
      )}
    </Screen>
  );
}

/** Platform — Audit Trail · `43:2` · rute `/platform/audit`. */
export function PlatformAudit() {
  const { t, locale } = useSession();
  const data = useResource(async () => (await endpoints.platformAudit(100)).items, []);
  const [actor, setActor] = useState('');

  return (
    <Screen
      title={t('platform.audit.title')}
      subtitle={t('platform.audit.subtitle')}
      resource={data}
      aside={
        <div className="filters">
          <Select
            label={t('platform.audit.filterActor')}
            value={actor}
            onChange={(e) => setActor(e.target.value)}
            options={[
              { value: '', label: t('platform.audit.allActors') },
              ...[...new Set((data.data ?? []).map((r) => r.actor_kind))].map((k) => ({
                value: k,
                label: t(`actorKind.${k}`),
              })),
            ]}
          />
        </div>
      }
    >
      {(items) => {
        const rows = actor === '' ? items : items.filter((r) => r.actor_kind === actor);
        return (
          <>
            {/*
              Jejak audit adalah append-only dan benar-benar tidak dapat dihapus (peta
              §5.8). Layar ini karena itu TIDAK punya satu pun kendali yang menulis —
              termasuk ekspor, yang menurut peta ikut tercatat dan yang rutenya
              (`POST /v1/platform/audit/exports`) belum punya handler.
            */}
            <AlertBanner
              kind="information"
              signalWord={t('signal.information')}
              title={t('platform.audit.appendOnly')}
            />
            {rows.length === 0 ? (
              <EmptyState title={t('platform.audit.empty.title')} body={t('platform.audit.empty.body')} />
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">{t('platform.audit.column.when')}</th>
                    <th scope="col">{t('platform.audit.column.actor')}</th>
                    <th scope="col">{t('platform.audit.column.event')}</th>
                    <th scope="col">{t('platform.audit.column.subject')}</th>
                    <th scope="col">{t('platform.audit.column.module')}</th>
                    <th scope="col">{t('platform.audit.column.basis')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td>{formatDateTime(row.occurred_at, locale)}</td>
                      {/*
                        KOLOM `SIAPA` — cacat 4, 28 September 2026.

                        Bentuk lamanya menaruh `t(actorKind.${row.actor_kind})` di KEDUA
                        baris, sehingga setiap pelaku tanpa nama berbunyi sama dua kali. Untuk
                        `SESSION_OPENED` dan `CREDENTIAL_READ` itu berarti "Belum masuk /
                        Belum masuk" — mengulang dirinya, DAN berbunyi seperti masuk yang
                        GAGAL padahal yang terjadi adalah masuk yang BERHASIL, di halaman yang
                        justru dibuat untuk dibaca orang pada hari perselisihan.

                        Keputusan apa yang ditulis ada di `lib/jejak-audit.ts` — dapat diuji
                        tanpa peramban, dan tanpa satu baris audit pun. Yang TIDAK dilakukan:
                        mengarang identitas. Baris itu memang tidak menyimpan nama.
                      */}
                      <td>
                        {(() => {
                          const p = pelakuJejakAudit(row);
                          if (p.jenis === 'bernama') {
                            return (
                              <>
                                {p.nama}
                                <br />
                                <span className="card__muted">{t(p.peranKunci)}</span>
                              </>
                            );
                          }
                          return (
                            <>
                              {t(p.namaKunci)}
                              {p.keteranganKunci ? (
                                <>
                                  <br />
                                  <span className="card__muted">{t(p.keteranganKunci)}</span>
                                </>
                              ) : null}
                            </>
                          );
                        })()}
                      </td>
                      <td>{row.event_code}</td>
                      <td>{row.subject_type}</td>
                      <td>{row.module_code ?? '—'}</td>
                      <td>
                        {row.processing_basis ? t(`privacy.basis.${row.processing_basis}`) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {/*
              CACAT 4 — penjelasan baris pra-sesi berdiri SEKALI, di sini, dan hanya ketika
              baris seperti itu benar-benar ada di layar. Bentuk pertama perbaikan ini
              menaruhnya di baris kedua SETIAP baris audit, dan tangkapan layarnya yang
              memutuskan: kalimat yang sama terulang empat puluh kali berturut-turut
              mengubah tabel yang seharusnya dibaca menjadi dinding teks. Cacat aslinya
              adalah dua baris yang mengulang dirinya; empat puluh bukan perbaikan.
            */}
            {adaBarisTanpaSesi(rows) ? (
              <p className="card__muted">{t('platform.audit.actor.preSessionNote')}</p>
            ) : null}
            {/*
              KALIMAT `platform.audit.exportPending` DIHAPUS PADA GELOMBANG M15, dan yang
              menggantikannya bukan tombol melainkan DUA kartu: jangkar rantai dan ekspor.
              Jangkar yang lebih penting di antara keduanya — layar ini menjanjikan jejak
              yang tidak dapat diubah "termasuk oleh pemegang basis datanya", dan sampai
              gelombang ini janji itu tidak punya satu pun elemen yang membuktikannya.
            */}
            <PanelJangkarDanEkspor />
          </>
        );
      }}
    </Screen>
  );
}

/** Platform — System Health · `43:425` · rute `/platform/health`. */
export function PlatformHealth() {
  const { t, locale } = useSession();
  const data = useResource(async () => endpoints.platformHealth(), []);

  return (
    <Screen
      title={t('platform.health.title')}
      subtitle={t('platform.health.subtitle')}
      resource={data}
      actions={
        <Button variant="secondary" onClick={() => void data.reload()}>
          {t('platform.health.refresh')}
        </Button>
      }
    >
      {(health) => {
        const angka = Object.entries(health).filter(
          ([key, value]) => key !== 'checked_at' && typeof value === 'number',
        ) as Array<[string, number]>;
        return (
          <>
            <div className="stat-grid">
              {angka.map(([key, value]) => (
                <StatCard
                  key={key}
                  label={t(`platform.health.metric.${key}`)}
                  value={String(value)}
                  /*
                    Merah HANYA untuk kondisi yang butuh tindakan manusia (peta §3):
                    kegagalan job dan insiden terbuka. Antrean yang berisi bukan kegagalan —
                    ia pekerjaan yang sedang menunggu, dan menandainya merah mengajari orang
                    bahwa merah tidak berarti apa-apa.
                  */
                  tone={
                    value > 0 && (key.includes('failed') || key.includes('failure') || key.includes('incident'))
                      ? 'danger'
                      : 'neutral'
                  }
                />
              ))}
            </div>
            <p className="card__muted">
              {t('platform.health.checkedAt', {
                at: formatDateTime(String(health.checked_at), locale),
              })}
            </p>
            {/*
              KALIMAT `platform.health.retryPending` DIHAPUS PADA GELOMBANG M15. Kedua hal
              yang dikatakannya belum bisa — menjalankan ulang pekerjaan yang gagal dan
              membuka insiden — berdiri di panel di bawah, dengan rutenya masing-masing.
            */}
            <PanelPekerjaanDanInsiden />
          </>
        );
      }}
    </Screen>
  );
}

/**
 * KONSOL KONSULTAN — PORTOFOLIO KLIEN · Figma `10:2` · rute `/portfolio` · izin KON.
 *
 * DUA PENYIMPANGAN DARI PETA, KEDUANYA PERLU DINYATAKAN
 * ---------------------------------------------------
 *  1. **Peta menempatkan layar ini di M13**, yang belum punya manifest. Rutenya hidup di
 *     M15 karena `consultant_assignment` adalah tabel M15 — lihat kepala
 *     `GET /v1/me/assignments` di `modules/M15/src/routes/platform.ts`.
 *  2. **Tidak ada "ringkasan kesehatan per klien"**, yang peta janjikan. Menepatinya
 *     menuntut agregat LINTAS tenant, yaitu tepat hal yang RLS tolak selama tenant belum
 *     terpasang pada baris sesi. Yang ada adalah tombol MASUK KE KLIEN, yang memanggil
 *     `POST /v1/auth/context/tenant` — dan perpindahan itu SELALU tercatat di jejak audit,
 *     seperti yang peta tuntut. Ringkasan yang diperoleh dengan melewati RLS bukan
 *     ringkasan; ia kebocoran dengan label lain.
 */
export function ClientPortfolio({ onSwitched }: { onSwitched: () => void }) {
  const { t, locale, translator } = useSession();
  const data = useResource(async () => (await endpoints.myAssignments()).items, []);
  const [switching, setSwitching] = useState<string | null>(null);
  const [problem, setProblem] = useState<ApiError | null>(null);

  async function masuk(slug: string) {
    setSwitching(slug);
    setProblem(null);
    try {
      await endpoints.switchTenant(slug);
      onSwitched();
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setSwitching(null);
    }
  }

  return (
    <Screen title={t('portfolio.title')} subtitle={t('portfolio.subtitle')} resource={data}>
      {(items) => (
        <>
          {problem ? (
            <AlertBanner
              kind="danger"
              signalWord={t('signal.danger')}
              title={renderProblem(translator, problem.problem)}
            />
          ) : null}

          {/* Perpindahan konteks SELALU tercatat, dan itu dikatakan sebelum ditekan. */}
          <AlertBanner
            kind="information"
            signalWord={t('signal.information')}
            title={t('portfolio.switchIsLogged')}
          />

          {items.length === 0 ? (
            <EmptyState title={t('portfolio.empty.title')} body={t('portfolio.empty.body')} />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('platform.column.tenant')}</th>
                  <th scope="col">{t('platform.column.plan')}</th>
                  <th scope="col">{t('platform.column.state')}</th>
                  <th scope="col">{t('portfolio.column.since')}</th>
                  <th scope="col">{t('locations.column.action')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.tenant_id}>
                    <td>
                      {row.tenant_name}
                      <br />
                      <span className="card__muted">{row.tenant_slug}</span>
                    </td>
                    <td>{row.plan_code ?? '—'}</td>
                    <td>
                      <Badge badgeTone={toneOfTenant(row.state)}>
                        {t(`platform.tenantState.${row.state}`)}
                      </Badge>
                    </td>
                    <td>{formatDate(row.granted_at, locale)}</td>
                    <td>
                      <Button
                        variant="primary"
                        busy={switching === row.tenant_slug}
                        disabled={row.state !== 'ACTIVE'}
                        onClick={() => void masuk(row.tenant_slug)}
                      >
                        {t('portfolio.enter')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <p className="card__muted">{t('portfolio.healthPending')}</p>
        </>
      )}
    </Screen>
  );
}

function toneOfTenant(state: string): string {
  switch (state) {
    case 'ACTIVE':
      return 'SUCCESS';
    case 'SUSPENDED':
      return 'DANGER';
    case 'PROVISIONING':
      return 'WARNING';
    default:
      return 'NEUTRAL';
  }
}

/**
 * Platform — Anggota · rute `/platform/memberships` · W-1 / C-16.
 *
 * KENAPA LAYAR INI ADA, DAN KENAPA TANPA IA RUTENYA TIDAK CUKUP
 * ------------------------------------------------------------
 * `platform_membership` menentukan siapa pegawai internal PQM yang boleh masuk, dan
 * sampai penutupan ini ia tidak punya satu pun penulis lewat produk. Rencana C-16
 * menuliskannya dengan kalimat yang tidak dapat ditawar: "Tanpa layar, rutenya hanya
 * memindahkan masalah dari `psql` ke `curl`." Seorang pegawai PQM baru yang tidak dapat
 * masuk adalah beban operasional; satu-satunya baris `PLT` aktif yang hilang tanpa jalan
 * pulih lewat produk adalah titik kegagalan.
 *
 * DUA HAL YANG LAYAR INI TOLAK LAKUKAN, dan keduanya disengaja:
 *
 *   1. **Ia tidak menetapkan kata sandi siapa pun.** Rekan baru lahir `INVITED` tanpa
 *      kata sandi dan menetapkannya sendiri lewat alur lupa kata sandi. Siapa pun yang
 *      dapat menetapkan kata sandi Anda dapat masuk sebagai Anda — dan sesudah itu jejak
 *      audit membawa nama Anda untuk perbuatannya.
 *   2. **Ia tidak menghitung sendiri siapa admin terakhir.** `is_last_active_admin`
 *      datang dari server. Sebuah layar yang menghitungnya dari daftar yang ia pegang
 *      akan salah pada saat dua orang membuka daftar yang sama, dan tombol `Cabut` yang
 *      hidup pada admin terakhir adalah tombol yang menjanjikan sesuatu yang trigger
 *      basis data akan menolak. Menjanjikan lalu menolak lebih buruk daripada tidak
 *      menawarkan.
 */
export function PlatformMembers() {
  const { t, translator, locale } = useSession();
  const data = useResource(async () => (await endpoints.platformMemberships()).items, []);

  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [roleCode, setRoleCode] = useState<'PLT' | 'KON'>('KON');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<ApiError | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [mencabut, setMencabut] = useState<string | null>(null);
  const [alasanCabut, setAlasanCabut] = useState('');

  const bolehBeri =
    email.trim() !== '' && fullName.trim().length >= 2 && reason.trim().length >= 3;

  async function beri() {
    setBusy(true);
    setProblem(null);
    try {
      await endpoints.platformMembershipSet({
        email: email.trim(),
        full_name: fullName.trim(),
        role_code: roleCode,
        reason: reason.trim(),
      });
      setEmail('');
      setFullName('');
      setReason('');
      setToast(t('platform.members.granted'));
      void data.reload();
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }

  async function cabut(userId: string) {
    setBusy(true);
    setProblem(null);
    try {
      await endpoints.platformMembershipRevoke(userId, { reason: alasanCabut.trim() });
      setMencabut(null);
      setAlasanCabut('');
      setToast(t('platform.members.revoked'));
      void data.reload();
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Screen
      title={t('platform.members.title')}
      subtitle={t('platform.members.subtitle')}
      resource={data}
    >
      {(items) => {
        const target = items.find((r) => r.user_id === mencabut) ?? null;
        return (
          <>
            {problem ? (
              <AlertBanner
                kind="danger"
                signalWord={t('signal.danger')}
                title={renderProblem(translator, problem.problem)}
              />
            ) : null}

            <section className="card">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">{t('platform.members.column.person')}</th>
                    <th scope="col">{t('platform.members.column.role')}</th>
                    <th scope="col">{t('platform.members.column.account')}</th>
                    <th scope="col">{t('platform.members.column.granted')}</th>
                    <th scope="col">{t('locations.column.action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => (
                    <tr key={r.user_id}>
                      <td>
                        {r.full_name ?? r.email}
                        <br />
                        <span className="card__muted">{r.email}</span>
                      </td>
                      <td>
                        <Badge tone={r.state === 'ACTIVE' ? 'information' : 'neutral'}>
                          {t(`platform.members.role.${r.role_code}`)}
                        </Badge>
                      </td>
                      <td>
                        {t(`users.state.${r.user_state}`)}
                        {r.has_password ? null : (
                          <>
                            <br />
                            <span className="card__muted">
                              {t('platform.members.noPassword')}
                            </span>
                          </>
                        )}
                      </td>
                      <td>
                        {formatDate(r.granted_at, locale)}
                        {r.granted_by_name ? (
                          <>
                            <br />
                            <span className="card__muted">{r.granted_by_name}</span>
                          </>
                        ) : null}
                      </td>
                      <td>
                        {r.state !== 'ACTIVE' ? null : r.is_last_active_admin ? (
                          /* Tidak ada tombol yang MATI di sini: yang ada adalah kalimat
                             yang mengatakan apa yang harus dilakukan lebih dulu. Sebuah
                             tombol nonaktif tanpa penjelasan adalah defect yang sudah
                             pernah ditemukan UAT (D-15) dalam bentuk lain. */
                          <span className="card__muted">{t('platform.members.lastAdmin')}</span>
                        ) : (
                          <Button
                            variant="secondary"
                            disabled={busy}
                            onClick={() => setMencabut(r.user_id)}
                          >
                            {t('platform.members.revoke')}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="card__muted">{t('platform.members.note')}</p>
            </section>

            <section className="card">
              <h2 className="card__title">{t('platform.members.grant')}</h2>
              <Input
                label={t('users.column.email')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Input
                label={t('users.column.person')}
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
              <Select
                label={t('platform.members.column.role')}
                value={roleCode}
                onChange={(e) => setRoleCode(e.target.value === 'PLT' ? 'PLT' : 'KON')}
                options={[
                  { value: 'KON', label: t('platform.members.role.KON') },
                  { value: 'PLT', label: t('platform.members.role.PLT') },
                ]}
              />
              <Input
                label={t('platform.members.reason')}
                value={reason}
                hint={t('platform.members.reason.hint')}
                onChange={(e) => setReason(e.target.value)}
              />
              <p className="card__muted">{t('platform.members.passwordPath')}</p>
              <p className="page__actions">
                <Button
                  variant="primary"
                  busy={busy}
                  disabled={!bolehBeri}
                  onClick={() => void beri()}
                >
                  {t('platform.members.grant')}
                </Button>
              </p>
            </section>

            <p className="card__muted">{t('platform.members.bootstrapNote')}</p>

            {target ? (
              <Dialog
                open
                kind="destructive"
                title={t('platform.members.confirmRevoke', {
                  name: target.full_name ?? target.email,
                })}
                description={t('platform.members.confirmRevokeBody')}
                confirmLabel={t('platform.members.revoke')}
                cancelLabel={t('app.cancel')}
                confirmDisabled={alasanCabut.trim().length < 3}
                onConfirm={() => void cabut(target.user_id)}
                onCancel={() => {
                  setMencabut(null);
                  setAlasanCabut('');
                }}
              >
                <Input
                  label={t('platform.members.reason')}
                  value={alasanCabut}
                  hint={t('platform.members.reason.hint')}
                  onChange={(e) => setAlasanCabut(e.target.value)}
                />
              </Dialog>
            ) : null}

            {toast ? (
              <Toast
                kind="success"
                signalWord={t('signal.success')}
                title={toast}
                onDismiss={() => setToast(null)}
                closeLabel={t('app.dismiss')}
              />
            ) : null}
          </>
        );
      }}
    </Screen>
  );
}

/**
 * Dua pembaca `branding` yang dipakai panel baca-saja sisi PQM.
 *
 * Bentuk `branding` didokumentasikan pada `comment on table tenant` (migrasi 0002) dan
 * dibaca APA ADANYA di sini — tanpa tipe yang dikarang, karena kolomnya jsonb dan
 * kontraknya menyatakannya `additionalProperties: true`. Sebuah tipe yang berpura-pura
 * tahu isinya akan berbohong pada hari kunci berikutnya ditambahkan.
 */
function brandingOf(tenant: unknown): Record<string, unknown> {
  const b = (tenant as { branding?: unknown } | null)?.branding;
  return b && typeof b === 'object' ? (b as Record<string, unknown>) : {};
}
function logoTerpasang(tenant: unknown): boolean {
  return typeof brandingOf(tenant).logo_mark_file_id === 'string';
}
function aksenTenant(tenant: unknown): string | null {
  const v = brandingOf(tenant).accent_hex;
  return typeof v === 'string' ? v : null;
}
